import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CreditCard, Check, ShieldCheck } from '@phosphor-icons/react';

// Load Stripe (will use test key from env or null for demo mode)
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY 
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// Card element styling to match habitué theme
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': {
        color: '#8BA89A',
      },
      iconColor: '#C3A365',
    },
    invalid: {
      color: '#FF6B6B',
      iconColor: '#FF6B6B',
    },
  },
  hidePostalCode: true,
};

// The actual form component that uses Stripe hooks
const StripePaymentForm = ({ 
  amount, 
  clientSecret, 
  onSuccess, 
  onError, 
  language 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
        },
      }
    );

    if (stripeError) {
      setError(stripeError.message);
      setLoading(false);
      if (onError) onError(stripeError);
    } else if (paymentIntent.status === 'succeeded') {
      setLoading(false);
      if (onSuccess) onSuccess(paymentIntent);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Card Input */}
      <div>
        <label className="block text-[#8BA89A] text-sm mb-3">
          {language === 'fr' ? 'Informations de carte' : 'Card details'}
        </label>
        <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-4 focus-within:border-[#C3A365]/50 transition-colors">
          <CardElement 
            options={cardElementOptions}
            onChange={(e) => setCardComplete(e.complete)}
          />
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Security badge */}
      <div className="flex items-center justify-center gap-2 text-[#8BA89A]">
        <ShieldCheck size={16} className="text-[#C3A365]" />
        <span className="text-xs">
          {language === 'fr' 
            ? 'Paiement sécurisé par Stripe' 
            : 'Secured by Stripe'}
        </span>
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={!stripe || loading || !cardComplete}
        className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-[#0F1A14] border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <CreditCard size={20} />
            {language === 'fr' ? `Payer £${amount}` : `Pay £${amount}`}
          </>
        )}
      </button>
    </form>
  );
};

// Demo mode component when Stripe is not configured
const DemoPaymentForm = ({ amount, onSuccess, language }) => {
  const [loading, setLoading] = useState(false);

  const handleDemoPayment = () => {
    setLoading(true);
    // Simulate payment processing
    setTimeout(() => {
      setLoading(false);
      if (onSuccess) {
        onSuccess({ id: `demo_${Date.now()}`, status: 'succeeded' });
      }
    }, 1500);
  };

  return (
    <div className="space-y-6">
      {/* Demo mode notice */}
      <div className="bg-[#C3A365]/10 border border-[#C3A365]/30 rounded-xl p-4">
        <p className="text-[#C3A365] text-sm text-center">
          {language === 'fr' 
            ? '🔧 Mode démo - Stripe non configuré' 
            : '🔧 Demo mode - Stripe not configured'}
        </p>
        <p className="text-[#8BA89A] text-xs text-center mt-1">
          {language === 'fr'
            ? 'Ajoutez REACT_APP_STRIPE_PUBLISHABLE_KEY pour activer les vrais paiements'
            : 'Add REACT_APP_STRIPE_PUBLISHABLE_KEY to enable real payments'}
        </p>
      </div>

      {/* Mock card input */}
      <div>
        <label className="block text-[#8BA89A] text-sm mb-3">
          {language === 'fr' ? 'Carte de test' : 'Test card'}
        </label>
        <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CreditCard size={24} className="text-[#C3A365]" />
            <div className="flex-1 font-mono text-white">
              4242 4242 4242 4242
            </div>
          </div>
          <p className="text-[#8BA89A] text-xs mt-2">
            12/25 · CVC: 123
          </p>
        </div>
      </div>

      <button
        onClick={handleDemoPayment}
        disabled={loading}
        className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-[#0F1A14] border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <Check size={20} weight="bold" />
            {language === 'fr' ? `Simuler £${amount}` : `Simulate £${amount}`}
          </>
        )}
      </button>
    </div>
  );
};

// Main wrapper component
const StripePayment = ({ 
  amount, 
  clientSecret, 
  onSuccess, 
  onError,
  language = 'fr'
}) => {
  // If Stripe is not configured, show demo mode
  if (!stripePromise) {
    return (
      <DemoPaymentForm 
        amount={amount} 
        onSuccess={onSuccess} 
        language={language}
      />
    );
  }

  // If we don't have a client secret yet, show loading
  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <StripePaymentForm
        amount={amount}
        clientSecret={clientSecret}
        onSuccess={onSuccess}
        onError={onError}
        language={language}
      />
    </Elements>
  );
};

export default StripePayment;
